import { useState, useEffect } from 'react';
import { X, Loader2, MessageSquare } from 'lucide-react';
import { purchasingAPI, SupplierQueryType, SupplierQueryPriority, ReasonCode } from '../../lib/api';
import { toast } from 'sonner';

interface CreateQueryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    query_type: SupplierQueryType;
    query_text: string;
    reference_value?: string;
    priority?: SupplierQueryPriority;
    reason_code?: number;
  }) => Promise<void>;
  partNumber?: string;
  batchSerialNo?: string;
}

export default function CreateQueryModal({
  isOpen,
  onClose,
  onSubmit,
  partNumber,
  batchSerialNo,
}: CreateQueryModalProps) {
  const [queryType, setQueryType] = useState<SupplierQueryType>('general');
  const [queryText, setQueryText] = useState('');
  const [referenceValue, setReferenceValue] = useState('');
  const [priority, setPriority] = useState<SupplierQueryPriority>('normal');
  const [reasonCodeId, setReasonCodeId] = useState<number | undefined>();
  const [reasonCodes, setReasonCodes] = useState<ReasonCode[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingReasonCodes, setLoadingReasonCodes] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchReasonCodes();
      // Pre-fill reference value based on query type
      if (queryType === 'part_number' && partNumber) {
        setReferenceValue(partNumber);
      } else if ((queryType === 'batch_number' || queryType === 'serial_number') && batchSerialNo) {
        setReferenceValue(batchSerialNo);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    // Update reference value when query type changes
    if (queryType === 'part_number' && partNumber) {
      setReferenceValue(partNumber);
    } else if ((queryType === 'batch_number' || queryType === 'serial_number') && batchSerialNo) {
      setReferenceValue(batchSerialNo);
    } else {
      setReferenceValue('');
    }
  }, [queryType, partNumber, batchSerialNo]);

  const fetchReasonCodes = async () => {
    try {
      setLoadingReasonCodes(true);
      const response = await purchasingAPI.getReasonCodes({ is_active: true });
      setReasonCodes(response.data.results || []);
    } catch (err) {
      console.error('Failed to fetch reason codes:', err);
    } finally {
      setLoadingReasonCodes(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!queryText.trim()) {
      toast.error('Please enter your query');
      return;
    }

    try {
      setSubmitting(true);
      await onSubmit({
        query_type: queryType,
        query_text: queryText.trim(),
        reference_value: referenceValue.trim() || undefined,
        priority,
        reason_code: reasonCodeId,
      });
      // Reset form
      setQueryType('general');
      setQueryText('');
      setReferenceValue('');
      setPriority('normal');
      setReasonCodeId(undefined);
    } catch (err) {
      // Error handling done in parent
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setQueryType('general');
      setQueryText('');
      setReferenceValue('');
      setPriority('normal');
      setReasonCodeId(undefined);
      onClose();
    }
  };

  const queryTypeOptions: { value: SupplierQueryType; label: string; description: string }[] = [
    { value: 'part_number', label: 'Part Number', description: 'Query about part number details' },
    { value: 'batch_number', label: 'Batch/Serial Number', description: 'Query about batch or lot number' },
    { value: 'serial_number', label: 'Serial Number', description: 'Query about specific serial number' },
    { value: 'delivery', label: 'Delivery', description: 'Query about delivery date or schedule' },
    { value: 'quantity', label: 'Quantity', description: 'Query about quantity details' },
    { value: 'general', label: 'General', description: 'General inquiry about this PO line' },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold">Raise a Query</h3>
          </div>
          <button
            onClick={handleClose}
            disabled={submitting}
            className="p-1 hover:bg-muted rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Query Type */}
          <div>
            <label className="block text-sm font-medium mb-2">Query Type *</label>
            <div className="grid grid-cols-2 gap-2">
              {queryTypeOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setQueryType(option.value)}
                  className={`p-3 text-left border rounded-lg transition-colors ${
                    queryType === option.value
                      ? 'border-primary bg-primary/5 ring-1 ring-primary'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium text-sm">{option.label}</div>
                  <div className="text-xs text-muted-foreground">{option.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Reference Value */}
          {(queryType === 'part_number' || queryType === 'batch_number' || queryType === 'serial_number') && (
            <div>
              <label className="block text-sm font-medium mb-1">
                {queryType === 'part_number' ? 'Part Number' :
                 queryType === 'batch_number' ? 'Batch/Serial Number' : 'Serial Number'}
              </label>
              <input
                type="text"
                value={referenceValue}
                onChange={(e) => setReferenceValue(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder={`Enter ${queryType.replace('_', ' ')}`}
              />
            </div>
          )}

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium mb-2">Priority</label>
            <div className="flex gap-2">
              {(['low', 'normal', 'high'] as SupplierQueryPriority[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPriority(p)}
                  className={`flex-1 px-3 py-2 border rounded-lg font-medium text-sm capitalize transition-colors ${
                    priority === p
                      ? p === 'high'
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : p === 'normal'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-500 bg-gray-50 text-gray-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Reason Code (Optional) */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Reason Code <span className="text-muted-foreground">(Optional)</span>
            </label>
            <select
              value={reasonCodeId || ''}
              onChange={(e) => setReasonCodeId(e.target.value ? Number(e.target.value) : undefined)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={loadingReasonCodes}
            >
              <option value="">Select a reason code</option>
              {reasonCodes.map((code) => (
                <option key={code.id} value={code.id}>
                  {code.code} - {code.description}
                </option>
              ))}
            </select>
          </div>

          {/* Query Text */}
          <div>
            <label className="block text-sm font-medium mb-1">Query Details *</label>
            <textarea
              value={queryText}
              onChange={(e) => setQueryText(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg h-32 resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Please describe your query in detail..."
              required
            />
          </div>
        </form>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 border rounded-lg hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !queryText.trim()}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <MessageSquare className="h-4 w-4" />
                Submit Query
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
